import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Plus, Trash2, Save, Upload } from 'lucide-react';
import { useCustomPeriodVariants, useCustomPeriods } from '../../hooks/useDateDimensionCustomPeriods';
import { dateDimensionCustomPeriodService } from '../../services/dateDimensionCustomPeriod.service';
import { EditableRegion, useIsReadOnly } from '../../contexts/ReadOnlyContext';

const NULL_SENTINEL = '__null__';

/** Try to parse a date string in YYYY-MM-DD or MM/DD/YYYY format and return YYYY-MM-DD, or null. */
function normalizeDate(raw: string): string | null {
  const trimmed = raw.trim();

  // YYYY-MM-DD
  const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    if (date.getFullYear() === Number(y) && date.getMonth() === Number(m) - 1 && date.getDate() === Number(d)) {
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return null;
  }

  // MM/DD/YYYY
  const usMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usMatch) {
    const [, m, d, y] = usMatch;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    if (date.getFullYear() === Number(y) && date.getMonth() === Number(m) - 1 && date.getDate() === Number(d)) {
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return null;
  }

  return null;
}

function parseCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        fields.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
  }
  fields.push(current.trim());
  return fields;
}

interface RowState {
  tempId: string;
  period_category: string;
  period_name: string;
  start_date: string;
  end_date: string;
}

const PERIOD_CATEGORIES = [
  { value: 'fiscal_month', label: 'Fiscal Month' },
  { value: 'fiscal_quarter', label: 'Fiscal Quarter' },
  { value: 'fiscal_half', label: 'Fiscal Half' },
  { value: 'fiscal_year', label: 'Fiscal Year' },
];

interface RowRefs {
  period_category: HTMLInputElement | null;
  period_name: HTMLInputElement | null;
  start_date: HTMLInputElement | null;
  end_date: HTMLInputElement | null;
}

export function ReportingPeriodsTab() {
  const readOnly = useIsReadOnly();
  const queryClient = useQueryClient();
  const [selectedVariant, setSelectedVariant] = useState<string>(NULL_SENTINEL);
  const [localVariants, setLocalVariants] = useState<string[]>([]);
  const [rows, setRows] = useState<RowState[]>([]);
  const [saving, setSaving] = useState(false);
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [newVariantName, setNewVariantName] = useState('');
  const rowRefsMap = useRef<Map<string, RowRefs>>(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);

  const actualVariant = selectedVariant === NULL_SENTINEL ? null : selectedVariant;

  const variantsQuery = useCustomPeriodVariants();
  const periodsQuery = useCustomPeriods(actualVariant);

  const allVariants = Array.from(
    new Set([...(variantsQuery.data || []), ...localVariants])
  ).sort();

  const makeEmptyRow = (): RowState => ({
    tempId: crypto.randomUUID(),
    period_category: '',
    period_name: '',
    start_date: '',
    end_date: '',
  });

  // Initialize rows from query data (always end with an empty row)
  useEffect(() => {
    if (periodsQuery.data) {
      const newRows: RowState[] = periodsQuery.data.map((p) => ({
        tempId: crypto.randomUUID(),
        period_category: p.period_category,
        period_name: p.period_name,
        start_date: p.start_date,
        end_date: p.end_date,
      }));
      newRows.push(makeEmptyRow());
      setRows(newRows);
      rowRefsMap.current.clear();
    }
  }, [periodsQuery.data]);

  const setRef = useCallback(
    (tempId: string, field: keyof RowRefs, el: HTMLInputElement | null) => {
      if (!rowRefsMap.current.has(tempId)) {
        rowRefsMap.current.set(tempId, {
          period_category: null,
          period_name: null,
          start_date: null,
          end_date: null,
        });
      }
      rowRefsMap.current.get(tempId)![field] = el;
    },
    []
  );

  const handleCategoryChange = (tempId: string, value: string) => {
    setRows((prev) =>
      prev.map((r) => (r.tempId === tempId ? { ...r, period_category: value } : r))
    );
    handleRowInput(tempId);
  };

  const handleRowInput = (tempId: string) => {
    // If the user typed into the last row, append a new empty row
    const lastRow = rows[rows.length - 1];
    if (lastRow && lastRow.tempId === tempId) {
      setRows((prev) => [...prev, makeEmptyRow()]);
    }
  };

  const removeRow = (tempId: string) => {
    setRows((prev) => {
      const filtered = prev.filter((r) => r.tempId !== tempId);
      // Always keep at least one empty row at the end
      if (filtered.length === 0) {
        return [makeEmptyRow()];
      }
      return filtered;
    });
    rowRefsMap.current.delete(tempId);
  };

  const handleCsvImport = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (!text) return;

      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length === 0) {
        toast.error('CSV file is empty.');
        return;
      }

      // Detect if first line is a header (check if columns 3 & 4 are NOT valid dates)
      const firstFields = parseCsvLine(lines[0]);
      const hasHeader =
        firstFields.length >= 4 &&
        normalizeDate(firstFields[2]) === null &&
        normalizeDate(firstFields[3]) === null;

      const dataLines = hasHeader ? lines.slice(1) : lines;
      const errors: string[] = [];
      const imported: RowState[] = [];

      dataLines.forEach((line, idx) => {
        const lineNum = hasHeader ? idx + 2 : idx + 1;
        const fields = parseCsvLine(line);

        if (fields.length < 4) {
          errors.push(`Row ${lineNum}: expected 4 columns, got ${fields.length}.`);
          return;
        }

        const [rawCategory, rawName, rawStart, rawEnd] = fields;

        if (!rawCategory) {
          errors.push(`Row ${lineNum}: period category is empty.`);
          return;
        }
        if (!rawName) {
          errors.push(`Row ${lineNum}: period name is empty.`);
          return;
        }

        const startDate = normalizeDate(rawStart);
        if (!startDate) {
          errors.push(`Row ${lineNum}: invalid start date "${rawStart}".`);
          return;
        }

        const endDate = normalizeDate(rawEnd);
        if (!endDate) {
          errors.push(`Row ${lineNum}: invalid end date "${rawEnd}".`);
          return;
        }

        // Normalize category to lowercase/underscore format (e.g. "Fiscal Month" -> "fiscal_month")
        const normalizedCategory = rawCategory.trim().toLowerCase().replace(/\s+/g, '_');

        imported.push({
          tempId: crypto.randomUUID(),
          period_category: normalizedCategory,
          period_name: rawName,
          start_date: startDate,
          end_date: endDate,
        });
      });

      if (errors.length > 0) {
        toast.error(`CSV import failed:\n${errors.slice(0, 5).join('\n')}${errors.length > 5 ? `\n...and ${errors.length - 5} more errors` : ''}`);
        return;
      }

      if (imported.length === 0) {
        toast.error('No valid rows found in CSV.');
        return;
      }

      // Append imported rows before the trailing empty row
      setRows((prev) => {
        const withoutTrailing = prev.filter((r, i) => {
          if (i !== prev.length - 1) return true;
          // Check if last row is empty (via refs or initial state)
          const refs = rowRefsMap.current.get(r.tempId);
          const cat = refs?.period_category?.value ?? r.period_category;
          const name = refs?.period_name?.value ?? r.period_name;
          const sd = refs?.start_date?.value ?? r.start_date;
          const ed = refs?.end_date?.value ?? r.end_date;
          return cat || name || sd || ed;
        });
        return [...withoutTrailing, ...imported, makeEmptyRow()];
      });
      rowRefsMap.current.clear();
      toast.success(`Imported ${imported.length} rows from CSV.`);
    };
    reader.readAsText(file);
  };

  const handleAddVariant = () => {
    const trimmed = newVariantName.trim();
    if (!trimmed) return;
    if (!allVariants.includes(trimmed)) {
      setLocalVariants((prev) => [...prev, trimmed]);
    }
    setSelectedVariant(trimmed);
    setNewVariantName('');
    setVariantDialogOpen(false);
  };

  const handleSave = async () => {
    const allPeriods = rows.map((row) => {
      const refs = rowRefsMap.current.get(row.tempId);
      return {
        period_category: row.period_category,
        period_name: refs?.period_name?.value ?? row.period_name,
        start_date: refs?.start_date?.value ?? row.start_date,
        end_date: refs?.end_date?.value ?? row.end_date,
      };
    });

    // Filter out completely empty rows
    const periods = allPeriods.filter(
      (p) => p.period_category || p.period_name || p.start_date || p.end_date
    );

    // Validate non-empty rows have all fields
    for (const p of periods) {
      if (!p.period_category || !p.period_name || !p.start_date || !p.end_date) {
        toast.error('All fields are required for each period.');
        return;
      }
    }

    setSaving(true);
    try {
      await dateDimensionCustomPeriodService.bulkSave(actualVariant, periods);
      await queryClient.invalidateQueries({ queryKey: ['custom-periods', actualVariant] });
      await queryClient.invalidateQueries({ queryKey: ['custom-period-variants'] });
      toast.success('Reporting periods saved.');
    } catch {
      toast.error('Failed to save reporting periods.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Top bar */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Variant:</span>
          {/* Chooses which variant you're looking at — a view selector, so it
              stays usable while the periods themselves are read-only. */}
          <EditableRegion>
          <Select value={selectedVariant} onValueChange={setSelectedVariant}>
            <SelectTrigger className="w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NULL_SENTINEL}>Default (no variant)</SelectItem>
              {allVariants.map((v) => (
                <SelectItem key={v} value={v}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          </EditableRegion>
        </div>
        {!readOnly && (
          <Button variant="outline" size="sm" onClick={() => setVariantDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add Variant
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleCsvImport(file);
              e.target.value = '';
            }}
          />
          {!readOnly && (
            <>
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                <Upload className="h-4 w-4 mr-1" />
                Import CSV
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-1" />
                {saving ? 'Saving...' : 'Save All'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto border rounded-md">
        <Table>
          <TableHeader className="sticky top-0 bg-background">
            <TableRow>
              <TableHead>Period Category</TableHead>
              <TableHead>Period Name</TableHead>
              <TableHead>Start Date</TableHead>
              <TableHead>End Date</TableHead>
              <TableHead className="w-[50px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row.tempId}>
                <TableCell>
                  <Select
                    value={row.period_category || undefined}
                    onValueChange={(value) => handleCategoryChange(row.tempId, value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {PERIOD_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Input
                    defaultValue={row.period_name}
                    ref={(el) => setRef(row.tempId, 'period_name', el)}
                    placeholder="e.g. March 2025"
                    onInput={() => handleRowInput(row.tempId)}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="date"
                    defaultValue={row.start_date}
                    ref={(el) => setRef(row.tempId, 'start_date', el)}
                    onInput={() => handleRowInput(row.tempId)}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="date"
                    defaultValue={row.end_date}
                    ref={(el) => setRef(row.tempId, 'end_date', el)}
                    onInput={() => handleRowInput(row.tempId)}
                  />
                </TableCell>
                <TableCell>
                  {!readOnly && index < rows.length - 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeRow(row.tempId)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add Variant Dialog */}
      <Dialog open={variantDialogOpen} onOpenChange={(open) => {
        setVariantDialogOpen(open);
        if (!open) setNewVariantName('');
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Variant</DialogTitle>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="variant-name">Variant Name</Label>
              <Input
                id="variant-name"
                value={newVariantName}
                onChange={(e) => setNewVariantName(e.target.value)}
                placeholder="e.g. fiscal_year"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newVariantName.trim()) {
                    handleAddVariant();
                  }
                }}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVariantDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddVariant} disabled={!newVariantName.trim()}>
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
